import {flatten} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PredicateOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {FilterDropdownRow} from '../../../../components/common/filters/multiSelectFilter/filterDropdownRow';
import {AnalyticsMetricsKeys} from '../../../../models/api/analyticsMetricsModel';
import {useAnalyticsSelectedMetrics} from '../../../helpers/hooks/useAnalyticsSelectedMetrics';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {ticketStatusHelpCenterUrl} from '../../../helpers/reportHelpers/analyticsReportHelpers';
import {MetricCategoriesEnum, ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {AnalyticsTooltip} from '../tooltips/analyticsTooltips';
import {ReportSectionMetricSelectionDropdownLabel} from './reportSectionMetricSelectionDropdownLabel';

/*
 * Constants.
 */

const maxSelectableMetrics = 3;

/*
 * Styles.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
`;

const StyledCategoryWrapperDiv = styled.div`
  &:not(:last-child) {
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  }
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  ${flexCenter};
  gap: 4px;
`;

const StyledLearnMoreA = styled.a`
  text-decoration: underline;
  color: ${(p) => p.theme.greys.white};
  font-weight: ${fontWeights.bold};
`;

/*
 * Props.
 */

interface MetricSelectionDropdownProps {
  reportSectionId: ReportElementIdsEnum;
  isDisabled: boolean;
  hasMetricMultiselect?: boolean; // If true will display the metric selection dropdown as a multiselect dropdown, otherwise as a single select dropdown.
  selectedMetricIds?: Array<AnalyticsMetricsKeys>; // Overrides the selected metric.
  metricSelectionDropdownPredicate?: PredicateOf<AnalyticsMetricsKeys>;
  onMetricSelection?: (selectedMetricIds: ReadonlyArray<AnalyticsMetricsKeys>) => void;
  isTable?: boolean;
  // If false, we won't fetch hidden metrics when selecting a new metric in the dropdown. This is typically used in tables where submetrics are already computed.
  shouldFetchHiddenMetrics: boolean;
}

/*
 * Component.
 * Metric selection dropdown for Analytics V3.
 */

export const ReportSectionMetricSelectorDropdown: FC<MetricSelectionDropdownProps> = ({
  reportSectionId,
  isDisabled,
  hasMetricMultiselect = false,
  selectedMetricIds,
  metricSelectionDropdownPredicate,
  onMetricSelection,
  isTable,
  shouldFetchHiddenMetrics,
}) => {
  const trackAnalytics = useAnalyticsTracking();
  const [hoveredRowIndex, setHoveredRowIndex] = useState<AnalyticsMetricsKeys>();

  const {
    displayedMetrics,
    selectedMetricIds: selectedMetricsFromStore,
    metricsByCategory,
    updateSelectedMetrics,
    maybeFetchHiddenMetrics,
  } = useAnalyticsSelectedMetrics(reportSectionId);
  const filteredSelectedMetrics = (selectedMetricIds || selectedMetricsFromStore).filter(
    (metricId) => metricSelectionDropdownPredicate?.(metricId) ?? true,
  );

  const trackMetricSelection = (metricId: AnalyticsMetricsKeys, isAdded: boolean) => {
    if (!hasMetricMultiselect) {
      trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_SELECT, {
        metric_type: metricId,
        report_section: reportSectionId,
      });
    } else {
      trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_LIST_EDIT, {
        metric_type: metricId,
        selection_type: isAdded ? 'add' : 'remove',
      });
    }
  };

  const trackDropdownClick = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_LIST_CLICK, {
      report_section: reportSectionId,
    });
  };

  const onSelection = (id: AnalyticsMetricsKeys) => {
    const isCurrentlySelected = filteredSelectedMetrics.includes(id);
    let newDisplayedMetrics;

    if (isCurrentlySelected && filteredSelectedMetrics.length === 1) {
      // Don't unselect the last/only metric.
      return;
    }

    if (hasMetricMultiselect) {
      if (isCurrentlySelected) {
        // Unselecting a metric.
        newDisplayedMetrics = filteredSelectedMetrics.filter((metric) => metric !== id);
      } else {
        if (shouldFetchHiddenMetrics) {
          // We selected a new metric. We may need to fetch it.
          maybeFetchHiddenMetrics(id);
        }
        newDisplayedMetrics = [...filteredSelectedMetrics, id];
      }
    } else {
      if (filteredSelectedMetrics[0] === id) {
        // Don't fetch hidden metrics if the selected metric didn't change.
        return;
      }
      if (!selectedMetricIds) {
        // We selected a new metric. We may need to fetch it (only if we are not handling the selected metric from outside the component).
        maybeFetchHiddenMetrics(id);
      }
      newDisplayedMetrics = [id];
    }

    trackMetricSelection(id, !isCurrentlySelected);

    // When selecting new metrics in the dropdown, we update the store. This will update the UI in return through the useEffect.
    updateSelectedMetrics(newDisplayedMetrics, reportSectionId);

    onMetricSelection?.(newDisplayedMetrics);
  };

  // Only display dropdown for report sections that have more than 1 metric.
  if (!flatten(Object.values(metricsByCategory)).length) {
    return null;
  }

  return (
    <StyledDropdownBoxCoordinator
      isDisabled={isDisabled}
      size={VisualSizesEnum.SMALL}
      value={getDropdownLabel(filteredSelectedMetrics, displayedMetrics, isTable)}
      onPopoverToggle={(isOpen) => (isOpen ? trackDropdownClick() : null)}
      renderDropdown={(popoverProps) => (
        <Dropdown {...popoverProps} width={260}>
          <DropdownList {...popoverProps}>
            {[
              MetricCategoriesEnum.VOLUME,
              MetricCategoriesEnum.EFFICIENCY,
              MetricCategoriesEnum.WORKFORCE,
            ].map((category) => {
              const selectableMetricConfigs = metricsByCategory[category];
              if (!selectableMetricConfigs?.length) {
                return null;
              }

              const selectableMetricsWithLabels = selectableMetricConfigs
                .filter(({id}) => metricSelectionDropdownPredicate?.(id) ?? true)
                .map((config) => ({
                  ...config,
                  labelAndTooltip: findLabelForMetricId(config.id, displayedMetrics, isTable),
                }));

              return (
                <StyledCategoryWrapperDiv key={category}>
                  <DropdownCategory>{getCategoryLabel(category)}</DropdownCategory>
                  {selectableMetricsWithLabels.map((metricConfigWithLabels) => {
                    const isSelected = filteredSelectedMetrics.includes(metricConfigWithLabels.id);
                    const isMaxItemsSelected =
                      !isTable && filteredSelectedMetrics.length >= maxSelectableMetrics;

                    const isRowDisabled =
                      metricConfigWithLabels.isDisabled || (isMaxItemsSelected && !isSelected);

                    return (
                      <div
                        onFocus={() => setHoveredRowIndex(metricConfigWithLabels.id)}
                        onMouseOver={() => setHoveredRowIndex(metricConfigWithLabels.id)}
                        onMouseLeave={() => setHoveredRowIndex(undefined)}
                        key={metricConfigWithLabels.id}
                      >
                        <FilterDropdownRow
                          row={{
                            isSelected,
                            isDisabled: isRowDisabled,
                            renderTooltip: () =>
                              isRowDisabled ? getDisabledTooltipLabel(isMaxItemsSelected) : false,
                            tooltipPersistent: true,
                            onSelect: () =>
                              metricConfigWithLabels.id &&
                              !isRowDisabled &&
                              onSelection(metricConfigWithLabels.id),
                            renderer: () => (
                              <StyledDropdownItemContent isChecked={isSelected}>
                                {metricConfigWithLabels.labelAndTooltip.label}{' '}
                                {hoveredRowIndex === metricConfigWithLabels.id && !isRowDisabled && (
                                  <AnalyticsTooltip
                                    tooltip={metricConfigWithLabels.labelAndTooltip.tooltip}
                                    reportSectionId={reportSectionId}
                                  />
                                )}
                              </StyledDropdownItemContent>
                            ),
                          }}
                          isMultiSelect={hasMetricMultiselect}
                          shouldCloseOnSelection={!hasMetricMultiselect}
                        />
                      </div>
                    );
                  })}
                </StyledCategoryWrapperDiv>
              );
            })}
          </DropdownList>
        </Dropdown>
      )}
    />
  );
};

/*
 * Helpers.
 */

function findLabelForMetricId(
  metricId: AnalyticsMetricsKeys,
  displayedMetrics: ReadonlyArray<AnalyticsMetricsKeys>,
  isTable?: boolean,
) {
  // this is for a table, metricId is a column id
  if (isTable && displayedMetrics.length === 1) {
    const metricLabels = getMetricLabels(displayedMetrics[0]);
    return (
      metricLabels?.columns?.[metricId] ??
      metricLabels ?? {
        id: metricId,
        label: undefined,
        tooltip: undefined,
      }
    );
  }

  return getMetricLabels(metricId);
}

function getDropdownLabel(
  defaultSelectedMetricIds: ReadonlyArray<AnalyticsMetricsKeys>,
  displayedMetrics: ReadonlyArray<AnalyticsMetricsKeys>,
  isTable?: boolean,
) {
  if (!defaultSelectedMetricIds.length) {
    return null;
  }

  const selectedMetricsWithLabels = defaultSelectedMetricIds.map(
    (e) => findLabelForMetricId(e, displayedMetrics, isTable).label,
  );

  return <ReportSectionMetricSelectionDropdownLabel selectedMetricLabels={selectedMetricsWithLabels} />;
}

function getDisabledTooltipLabel(isMaxItemsSelected: boolean) {
  if (isMaxItemsSelected) {
    return <Trans id="analytics-metric-picker-disabled-default">You can select up to 3 metrics</Trans>;
  }

  return (
    <Trans id="analytics-metric-picker-disabled-missing-ticketing">
      Access this metric by enabling ticket statuses.
      <br />
      <StyledLearnMoreA href={ticketStatusHelpCenterUrl} target="_blank" rel="noreferrer">
        Learn more
      </StyledLearnMoreA>
    </Trans>
  );
}

function getCategoryLabel(category: MetricCategoriesEnum) {
  switch (category) {
    case MetricCategoriesEnum.VOLUME:
      return <Trans id="analytics-metrics-category-volume">Volume</Trans>;
    case MetricCategoriesEnum.EFFICIENCY:
      return <Trans id="analytics-metrics-category-efficiency">Efficiency</Trans>;
    case MetricCategoriesEnum.WORKFORCE:
      return <Trans id="analytics-metrics-category-workforce">Workforce</Trans>;
    default:
      throw new UnreachableError(category);
  }
}
